import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle, FontSize, FontFamily } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Heading1,
  Heading2,
  Heading3,
  RemoveFormatting,
  Quote,
  Undo,
  Redo,
} from 'lucide-react';

/* ---------------- Helpers ---------------- */
const FONT_FAMILIES = [
  { label: 'Sans serif', value: 'Inter, system-ui, sans-serif' },
  { label: 'Serif', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Mono', value: '"JetBrains Mono", "Fira Code", monospace' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
];

const FONT_SIZES = ['12px', '13px', '14px', '15px', '16px', '18px', '20px', '24px', '28px', '32px'];

/**
 * Detect plain text vs HTML and convert plain text to HTML preserving paragraphs.
 * Used for backwards-compat with existing plain-text descriptions stored in the DB.
 */
export const descToHtml = (value) => {
  if (!value) return '';
  // Already HTML — keep as is
  if (/^\s*<(p|h\d|ul|ol|div|span|strong|em|br)[\s>]/i.test(value)) return value;
  // Plain text → split on blank lines, keep single line breaks as <br>
  const escape = (s) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const paras = value
    .split(/\n{2,}/)
    .map((p) => `<p>${escape(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
  return paras || `<p>${escape(value)}</p>`;
};

/**
 * Convert HTML (or plain text) to clean plain text preserving block-level
 * paragraph breaks and bullet markers. Suitable for pasting into WhatsApp,
 * email, or any plain-text-only target.
 */
export const htmlToPlainText = (value) => {
  if (!value) return '';
  // Already plain text — return as is
  if (!/<[a-z][\s\S]*?>/i.test(value)) return value;

  // Use a detached DOM node so the browser does the heavy lifting.
  const doc = new DOMParser().parseFromString(value, 'text/html');

  const walk = (node) => {
    if (node.nodeType === 3) return node.nodeValue || '';   // text node
    if (node.nodeType !== 1) return '';                      // ignore comments etc.
    const tag = node.tagName.toLowerCase();
    let inner = Array.from(node.childNodes).map(walk).join('');
    if (tag === 'br') return '\n';
    if (tag === 'li') {
      // Bullet for ul, "1. " style for ol; parent decides via indent
      const parent = node.parentElement?.tagName.toLowerCase();
      const marker = parent === 'ol' ? '• ' : '• ';
      return `${marker}${inner.trim()}\n`;
    }
    if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote'].includes(tag)) {
      return `${inner.trim()}\n\n`;
    }
    if (tag === 'ul' || tag === 'ol') {
      return `${inner.trimEnd()}\n\n`;
    }
    return inner;
  };

  return walk(doc.body)
    .replace(/\n{3,}/g, '\n\n')   // collapse runs of blank lines
    .trim();
};

/* ---------------- ToolbarButton ---------------- */
const ToolbarButton = ({ active, onClick, title, children, testid }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    data-testid={testid}
    className={`h-8 w-8 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition disabled:opacity-40 disabled:hover:bg-transparent ${
      active ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' : ''
    }`}
  >
    {children}
  </button>
);

const Sep = () => <span className="w-px h-5 bg-slate-200 mx-0.5" />;

/* ---------------- RichTextEditor ---------------- */
const RichTextEditor = ({ value, onChange, placeholder = 'Start typing...', minHeight = 280 }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      FontFamily,
      FontSize,
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      // TipTap returns "<p></p>" for empty doc — normalise to empty string
      onChange?.(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-slate max-w-none focus:outline-none px-4 py-3 [&_p]:my-2 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:my-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:my-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_blockquote]:border-l-4 [&_blockquote]:border-slate-200 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-600',
        'data-testid': 'rich-text-editor-content',
        'data-placeholder': placeholder,
      },
    },
  });

  /* Sync external value changes (e.g., AI gen / PDF upload populates the field) */
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (incoming !== current && (incoming || current !== '<p></p>')) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  /* Current font family / size for the dropdowns */
  const currentFontFamily = editor.getAttributes('textStyle').fontFamily || '';
  const currentFontSize = editor.getAttributes('textStyle').fontSize || '';

  return (
    <div
      className="border border-slate-200 rounded-md bg-white focus-within:border-teal-300 focus-within:ring-2 focus-within:ring-teal-100 transition"
      data-testid="rich-text-editor"
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50/60 rounded-t-md">
        {/* Undo / Redo */}
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          title="Undo (Ctrl+Z)"
          testid="rte-undo"
        >
          <Undo className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          title="Redo (Ctrl+Shift+Z)"
          testid="rte-redo"
        >
          <Redo className="w-4 h-4" />
        </ToolbarButton>

        <Sep />

        {/* Font family */}
        <select
          value={currentFontFamily}
          onChange={(e) => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontFamily(v).run();
            else editor.chain().focus().unsetFontFamily().run();
          }}
          title="Font family"
          data-testid="rte-font-family"
          className="h-8 text-xs px-2 border border-slate-200 rounded-md bg-white text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-100 cursor-pointer max-w-[120px]"
        >
          <option value="">Default font</option>
          {FONT_FAMILIES.map((f) => (
            <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
              {f.label}
            </option>
          ))}
        </select>

        {/* Font size */}
        <select
          value={currentFontSize}
          onChange={(e) => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontSize(v).run();
            else editor.chain().focus().unsetFontSize().run();
          }}
          title="Font size"
          data-testid="rte-font-size"
          className="h-8 text-xs px-2 border border-slate-200 rounded-md bg-white text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-teal-100 cursor-pointer w-[72px]"
        >
          <option value="">Size</option>
          {FONT_SIZES.map((s) => (
            <option key={s} value={s}>
              {s.replace('px', '')}
            </option>
          ))}
        </select>

        <Sep />

        {/* Headings */}
        <ToolbarButton
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          title="Heading 1"
          testid="rte-h1"
        >
          <Heading1 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading 2"
          testid="rte-h2"
        >
          <Heading2 className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title="Heading 3"
          testid="rte-h3"
        >
          <Heading3 className="w-4 h-4" />
        </ToolbarButton>

        <Sep />

        {/* Bold / Italic / Underline */}
        <ToolbarButton
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold (Ctrl+B)"
          testid="rte-bold"
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic (Ctrl+I)"
          testid="rte-italic"
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline (Ctrl+U)"
          testid="rte-underline"
        >
          <UnderlineIcon className="w-4 h-4" />
        </ToolbarButton>

        <Sep />

        {/* Lists */}
        <ToolbarButton
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet list"
          testid="rte-bullet-list"
        >
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered list"
          testid="rte-ordered-list"
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="Quote"
          testid="rte-quote"
        >
          <Quote className="w-4 h-4" />
        </ToolbarButton>

        <Sep />

        {/* Alignment */}
        <ToolbarButton
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          title="Align left"
          testid="rte-align-left"
        >
          <AlignLeft className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          title="Align center"
          testid="rte-align-center"
        >
          <AlignCenter className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          title="Align right"
          testid="rte-align-right"
        >
          <AlignRight className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: 'justify' })}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          title="Justify"
          testid="rte-align-justify"
        >
          <AlignJustify className="w-4 h-4" />
        </ToolbarButton>

        <Sep />

        {/* Clear formatting */}
        <ToolbarButton
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          title="Clear formatting"
          testid="rte-clear-format"
        >
          <RemoveFormatting className="w-4 h-4" />
        </ToolbarButton>
      </div>

      {/* Editor body */}
      <div className="overflow-auto" style={{ minHeight, maxHeight: 480 }}>
        <EditorContent editor={editor} className="h-full" />
      </div>
    </div>
  );
};

export default RichTextEditor;
